import { z } from "zod"
import { type Address, isAddress } from "viem"
import { type VotingOptionType, VotingOptionSchema } from "@antipartido-inc/contracts-ifaces"
import { VotingDoc } from "./VotingDoc.js"

const VotingOptionValidation = z.object({
  id: z.string({ required_error: "id is required" }),
  title: z.string({ required_error: "title is required" }),
  description: z.string({ required_error: "description is required" }),
  votes: z.number({ required_error: "votes is required" }),
  key: z.string({ required_error: "key is required" }),
  percentage: z.number().optional(),
  docLinks: z.array(z.instanceof(VotingDoc)).optional(),
  candidateImage: z.instanceof(VotingDoc).optional(),
  sinkAddress: z.string().refine(isAddress).optional(),
})

export class VotingOption {
  constructor(
    private readonly _id: string,
    private readonly _title: string,
    private readonly _description: string,
    private readonly _votes: number,
    private readonly _key: string,
    private readonly _percentage?: number,
    private readonly _docLinks?: VotingDoc[],
    private readonly _candidateImage?: VotingDoc,
    private readonly _sinkAddress?: Address,
  ) {}

  static create({
    id,
    title,
    description,
    votes,
    key,
    percentage,
    docLinks,
    candidateImage,
    sinkAddress,
  }: z.infer<typeof VotingOptionValidation>) {
    const { success, error } = VotingOptionValidation.safeParse({
      id,
      title,
      description,
      votes,
      key,
      percentage,
      docLinks,
      candidateImage,
      sinkAddress,
    })
    if (!success) throw new Error(error.message)
    return new VotingOption(id, title, description, votes, key, percentage, docLinks, candidateImage, sinkAddress)
  }

  static createFromAPI({
    id,
    title,
    description,
    votes,
    key,
    percentage,
    docLinks,
    candidateImage,
    sinkAddress,
  }: VotingOptionType) {
    const { success, error } = VotingOptionSchema.safeParse({
      id,
      title,
      description,
      votes,
      key,
      percentage,
      docLinks,
      candidateImage,
      sinkAddress,
    })
    if (!success) throw new Error(error.message)

    const docs = docLinks?.map((doc) => VotingDoc.createFromAPI(doc))
    const image = candidateImage ? VotingDoc.createFromAPI(candidateImage) : undefined

    return VotingOption.create({
      id,
      title,
      description,
      votes,
      key,
      percentage: percentage ?? undefined,
      docLinks: docs,
      candidateImage: image,
      sinkAddress: sinkAddress ? (sinkAddress as Address) : undefined,
    })
  }

  get id() { return this._id } // prettier-ignore
  get title() { return this._title } // prettier-ignore
  get description() { return this._description } // prettier-ignore
  get votes() { return this._votes } // prettier-ignore
  get key() { return this._key } // prettier-ignore
  get percentage() { return this._percentage } // prettier-ignore
  get docLinks() { return this._docLinks } // prettier-ignore
  get candidateImage() { return this._candidateImage } // prettier-ignore
  get sinkAddress() { return this._sinkAddress } // prettier-ignore

  serialize() {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      votes: this.votes,
      key: this.key,
      percentage: this.percentage,
      docLinks: this.docLinks?.map((doc) => doc.serialize()),
      candidateImage: this.candidateImage?.serialize(),
      sinkAddress: this.sinkAddress,
    }
  }
}

export type VotingOptionSerialized = ReturnType<VotingOption["serialize"]>
